import React, { Component } from "react";
import { observer } from "mobx-react";
import { Trans, withTranslation } from "react-i18next";
/** @typedef {import("../sql/schemaDatatypes").SavedPlaylistSql} SavedPlaylistSql */
import SearchState, { LOADING_INDICATOR_HEIGHT } from "./SearchState";
import "./Search.scss";
/** @typedef {import("@shared/typedefs").Tag} Tag */
import MusicTrack from "./components/MusicTrack/MusicTrack";
import MusicTrackState from "./components/MusicTrack/MusicTrackState";
import VideoSync from "./components/VideoSync/VideoSync";
import dragPercentage from "./utils/dragPercentage";
import soundIconSvg from "./icons/sound.svg?raw";
import Popup from "./components/Popup/Popup";
import Share from "./components/Share/Share";
import LoginFlow from "./components/LoginFlow/LoginFlow";
import MyAccount from "./components/MyAccount/MyAccount";

import headerLogoPng from "./as-logo2015-v1.png";
import UploadTrack from "./components/UploadTrack/UploadTrack";
import AdminTrackEditor from "./components/AdminTrackEditor/AdminTrackEditor";

import AddToPlaylist from "./components/AddToPlaylist/AddToPlaylist";
import { updateCollectionPageSchema } from "./schemaMarkup";
import parseTrackCount from "./utils/parseTrackCount";
import { allTags } from "@shared/constants";
import starBg from "./icons/star_bg.png";

/**
 * @typedef {Object} MusicListProps
 * @property {SearchState} store - The main search state store
 * @property {number} musicLength - Total number of music tracks
 * @property {number} musicListScrollTop - Current scroll position
 * @property {number} musicListHeight - Height of the music list container
 * @property {number} musicListWidth - Width of the music list container
 * @property {Array<number>} itemSizes - Array of item sizes for virtualization
 * @property {Array<string>} itemIds - Array of track IDs
 * @property {boolean} loadingMusic - Whether music is currently loading
 */

class MusicList extends Component {
  componentDidMount() {
    this.forceUpdate();
  }

  componentDidUpdate() {
    // Check if we need to load more music after render is complete
    this.checkLoadMoreMusic();
  }

  checkLoadMoreMusic() {
    if (this.props.loadingMusic) return;

    const itemCount =
      this.props.store.visibleMusic.length +
      (this.props.loadingMusic || this.props.store.hasMoreMusic ? 1 : 0);

    const currentTop = this.props.musicListScrollTop * -1;
    const currentBottom = currentTop + this.props.musicListHeight;

    let offsetCursor = 0;
    for (let i = 0; i < itemCount; i += 1) {
      const itemHeight = this.getItemSize(i);
      const currentVisible =
        currentTop < offsetCursor + itemHeight && offsetCursor < currentBottom;

      if (i === this.props.store.visibleMusic.length) {
        // This is the loading indicator
        if (this.props.store.hasMoreMusic && currentVisible) {
          // Use setTimeout to avoid state updates during render
          setTimeout(() => {
            if (!this.props.loadingMusic) {
              this.props.store.loadNextPageOfMusic();
            }
          }, 0);
          break;
        }
      }
      offsetCursor += itemHeight;
    }
  }

  /**
   * @param {MusicListProps} nextProps
   * @returns {boolean}
   */
  shouldComponentUpdate(nextProps) {
    // Always update when the music list changes
    if (nextProps.musicLength !== this.props.musicLength) return true;
    if (nextProps.musicListHeight !== this.props.musicListHeight) return true;
    if (nextProps.musicListWidth !== this.props.musicListWidth) return true;
    if (nextProps.itemSizes.length !== this.props.itemSizes.length) return true;
    for (let i = 0; i < nextProps.itemSizes.length; i += 1) {
      if (nextProps.itemSizes[i] !== this.props.itemSizes[i]) return true;
    }
    if (nextProps.itemIds.length !== this.props.itemIds.length) return true;
    for (let i = 0; i < nextProps.itemIds.length; i += 1) {
      if (nextProps.itemIds[i] !== this.props.itemIds[i]) return true;
    }
    if (nextProps.loadingMusic !== this.props.loadingMusic) return true;
    // Update when language changes
    if (nextProps.store.currentLanguage !== this.props.store.currentLanguage) return true;

    const itemCount =
      this.props.store.visibleMusic.length +
      (this.props.loadingMusic || this.props.store.hasMoreMusic ? 1 : 0);

    const currentTop = this.props.musicListScrollTop * -1;
    const currentBottom = currentTop + this.props.musicListHeight;
    const nextTop = nextProps.musicListScrollTop * -1;
    const nextBottom = nextTop + nextProps.musicListHeight;

    let offsetCursor = 0;
    for (let i = 0; i < itemCount; i += 1) {
      const itemHeight = this.getItemSize(i);
      const currentVisible =
        currentTop < offsetCursor + itemHeight && offsetCursor < currentBottom;
      const nextVisible =
        nextTop < offsetCursor + itemHeight && offsetCursor < nextBottom;
      if (currentVisible !== nextVisible) {
        return true;
      }
      offsetCursor += itemHeight;
    }

    return false;
  }

  componentWillUnmount() {}

  /**
   * @param {number} index
   */
  getItemSize = (index) => {
    // This means it's a loading indicator
    if (index >= this.props.store.visibleMusic.length) {
      return LOADING_INDICATOR_HEIGHT;
    }
    const music = this.props.store.visibleMusic[index];
    if (music != null) {
      return music.getHeight();
    }
    return LOADING_INDICATOR_HEIGHT;
  };

  render() {
    // Create the virtualized list to buffer all our beautiful tracks into the DOM

    const addLoadingTrack = this.props.store.showingLoadingTrack();
    const itemCount =
      this.props.store.visibleMusic.length + (addLoadingTrack ? 1 : 0);
    const musicListEntries = [];

    if (this.props.store.isMobileWindow) {
      for (let i = 0; i < itemCount; i += 1) {
        const style = {};

        if (i < this.props.store.visibleMusic.length) {
          const music = this.props.store.visibleMusic[i];
          musicListEntries.push(
            <MusicTrack
              store={music}
              key={music.music.id}
              depth={0}
              dragIndex={i}
              positioningStyle={style}
              isLastTrack={i === this.props.store.visibleMusic.length - 1}
            />
          );
        } else {
          musicListEntries.push(
            <div style={style} key={"loading"}>
              <div
                className={"Search-loading-indicator-container"}
                key={"loading"}
              >
                <div className={"Search-loading-indicator-outer"}>
                  <div className={"Search-loading-indicator-ring-holder"}>
                    <div className={"Search-loading-indicator-ring"} />
                  </div>
                </div>
                <div className={"Search-loading-indicator-text"}>
                  Loading...
                </div>
              </div>
            </div>
          );
          this.props.store.loadNextPageOfMusic();
        }
      }
    } else {
      const top = this.props.musicListScrollTop * -1;
      const bottom = top + this.props.musicListHeight;

      let offsetCursor = 0;
      for (let i = 0; i < itemCount; i += 1) {
        const itemHeight = this.getItemSize(i);
        if (
          top < offsetCursor + itemHeight && // below the top
          offsetCursor < bottom // above the bottom
        ) {
          let style = {};
          if (!this.props.store.isMobileWindow) {
            style = {
              position: "absolute",
              left: "0px",
              top: `${offsetCursor}px`,
              width: `${this.props.musicListWidth}px`
            };
          }

          // This means it's a normal music track
          if (i < this.props.store.visibleMusic.length) {
            const music = this.props.store.visibleMusic[i];
            musicListEntries.push(
              <MusicTrack
                store={music}
                key={music.music.id}
                depth={0}
                dragIndex={i}
                positioningStyle={style}
                isLastTrack={i === this.props.store.visibleMusic.length - 1}
              />
            );
          } else {
            // Otherwise it's a loading track
            musicListEntries.push(
              <div style={style} key={"loading"}>
                <div
                  className={"Search-loading-indicator-container"}
                  key={"loading"}
                >
                  <div className={"Search-loading-indicator-outer"}>
                    <div className={"Search-loading-indicator-ring-holder"}>
                      <div className={"Search-loading-indicator-ring"} />
                    </div>
                  </div>
                  <div className={"Search-loading-indicator-text"}>
                    Loading...
                  </div>
                </div>
              </div>
            );
            // Note: Load more logic moved to componentDidUpdate to avoid state updates during render
          }
        }
        offsetCursor += itemHeight;
      }
    }

    return (
      <div
        className={"Search-music-rows"}
        ref={r => this.props.store.setMusicDiv(r)}
        style={{
          height: 0, // `${this.props.musicListHeight}px`,
          width: `${this.props.musicListWidth}px`
        }}
      >
        {musicListEntries}
      </div>
    );
  }
}

/**
 * @typedef {Object} SearchInternalsProps
 * @property {SearchState} store - The main search state store
 * @property {boolean} blur - Whether to apply blur effect
 */

@observer
class SearchInternals extends Component {
  render() {
    // Access currentLanguage to ensure re-render on language change
    const lang = this.props.store.currentLanguage;

    let genreColumns;
    let tagRows;
    let genresLeftGutter;
    let tagsLeftGutter;
    let tagsLeftPadding;
    let tagsTable;
    let videoSyncTable;
    let subgenresMobile;

    let activeTags = [];
    const genreElements = [];
    const keywordElements = [];

    this.props.store.genres.forEach((tags) => {
      tags.forEach((tag) => {
        if (this.props.store.isTagActive(tag.id)) {
          activeTags.push(
            <span
              onClick={() => this.props.store.toggleTagActive(tag.id)}
              key={`activetags__${tag.id}`}
            >
              {tag.tag_name}
            </span>
          );
        }
        genreElements.push(
          <li
            className={`genre-parent ${
              this.props.store.isTagActive(tag.id) ? "active" : ""
            }`}
            key={`genre__${tag.id}`}
            onClick={() => this.props.store.toggleTagActive(tag.id)}
          >
            <Trans>{tag.tag_name.toLowerCase()}</Trans>
          </li>
        );
        if (
          tag.children != null &&
          tag.tag_name.toLowerCase() === "international"
        ) {
          for (let j = 0; j < tag.children.length; j += 1) {
            /** @type {Tag} */
            const subGenre = tag.children[j];

            genreElements.push(
              <li
                className={`genre-child ${
                  this.props.store.isTagActive(tag.id) ? "show" : ""
                } ${this.props.store.isTagActive(subGenre.id) ? "active" : ""}`}
                key={`subGenre__${subGenre.id}`}
                onClick={() => this.props.store.toggleTagActive(subGenre.id)}
              >
                <Trans>{subGenre.tag_name.toLowerCase()}</Trans>
              </li>
            );
          }
        }
      });
    });
    this.props.store.tags.forEach((tag) => {
      if (this.props.store.isTagActive(tag.id)) {
        activeTags.push(
          <span
            onClick={() => this.props.store.toggleTagActive(tag.id)}
            key={`activetags__${tag.id}`}
          >
            {tag.tag_name}
          </span>
        );
      }
      keywordElements.push(
        <li
          className={`tag-parent ${
            this.props.store.isTagActive(tag.id) ? " active" : ""
          }`}
          onClick={() => this.props.store.toggleTagActive(tag.id)}
          key={`tag__${tag.id}`}
          title={"select multiple keywords or genres to focus search"}
        >
          <Trans>{tag.tag_name.toLowerCase()}</Trans>
        </li>
      );
    });

    activeTags = [...new Set(activeTags)];

    let filterButtonText = `filter`;

    if (this.props.store.activeTags.length > 0) {
      // filterButtonText = `Edit Filters (${this.props.store.activeTags.length})`;
      filterButtonText = "edit filters";
    }

    if (this.props.store.mobileActive) {
      filterButtonText = `view ${
        this.props.store.searchType === "favorites"
          ? this.props.store.visibleMusic.length
          : this.props.store.musicCount
      } results`;
      if (this.props.store.musicCount === 0) {
        filterButtonText = `select keywords to search`;
      }
    }

    if (this.props.store.showingPlaylistListing) {
      filterButtonText = `new search`;
    }

    let spanVerbiage = "";
    if (activeTags.length > 3) {
      spanVerbiage = "+";
      spanVerbiage +=
        activeTags.length > 3 ? activeTags.length - 3 : activeTags.length;
    }

    if (this.props.store.showingAllSelectedMobile) {
      spanVerbiage = "show less";
    }

    let autocompleteEntries = "";
    if (this.props.store.searchBarContents) {
      autocompleteEntries = this.props.store.autocompleteTracks.map(
        (track) => (
          <div
            key={track.toString()}
            onMouseDown={() => {
              // this.props.store.setSearchBarContents(track);
              this.props.store.doKeywordSearch(
                this.props.store.searchBarContents
              );
              this.props.store.toggleMobileMenu();
              this.props.store.removeAutocompleteTracks();
            }}
          >
            {track
              .toString()
              .toLowerCase()
              .substring(0, this.props.store.searchBarContents.length)}
            <b>
              {track
                .toString()
                .toLowerCase()
                .substring(this.props.store.searchBarContents.length)}
            </b>
          </div>
        )
      );
    }

    let mobilePlaylists;
    let existingSearches = [];
    if (this.props.store.showingPlaylistListing) {
      existingSearches = this.props.store.myAccountState.savedTracksOrganized();

      mobilePlaylists = existingSearches.map((search) => (
        <li
          key={search.id}
          onMouseDown={() => {
            this.props.store.togglePlaylistListing();
            this.props.store.myAccountState.viewSearch(search.id);
          }}
        >
          <span className={"playlist-name"}>{search.name}</span>
          <span className={"icon dots"} />
          <span className={"track-total"}>
            {`${parseTrackCount(search.tracks, search.track_count)} tracks`}
          </span>
        </li>
      ));
    }

    let favoritesMenuMobile;

    if (this.props.store.showingFavoritesMenu) {
      favoritesMenuMobile = (
        <ul className={`mobile-dropdown favorites-actions`}>
          <li
            className={`icon share`}
            onMouseDown={() => {
              this.props.store.setShortlinkType("playlist");
              this.props.store.toggleSharePopup();
              this.props.store.toggleFavoritesMenu();
            }}
          >
            Share
          </li>
          <li
            className={"icon add-light"}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.saveSearch();
              this.props.store.toggleFavoritesMenu();
            }}
          >
            Save as playlist
          </li>
          <li
            className={"icon delete"}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.clearAllLikes();
              this.props.store.toggleFavoritesMenu();
            }}
          >
            Clear favorites
          </li>
          <li
            className={"Menus-blanket"}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.toggleFavoritesMenu();
            }}
          />
        </ul>
      );
    }

    const mobileSearch = (
      <div
        className={`Search-mobile-interface ${
          this.props.store.mobileActive ? "active" : ""
        }`}
      >
        <input
          className={"Search-mobile-interface-text-search"}
          placeholder={"describe your sound or scene"}
          autoComplete={"off"}
          autoCorrect={"off"}
          autoCapitalize={"off"}
          spellCheck={"false"}
          value={this.props.store.aiSearchQuery}
          onChange={e => {
            this.props.store.aiSearchQuery = e.target.value;
          }}
          onKeyDown={e => {
            e.stopPropagation();
            if (e.key === "Enter") {
              e.preventDefault();
              this.props.store.doAiSearch(this.props.store.aiSearchQuery);
              e.target.blur();
            }
          }}
        />

        <div className={`Search-mobile-autocomplete-holder`}>
          {autocompleteEntries}
        </div>

        <div className={"favorites-header"}>
          <h3
            className={`align-left ${
              this.props.store.mobileActive ||
              this.props.store.searchType !== "favorites"
                ? "mobile-hide"
                : ""
            }`}
          >
            {this.props.store.searchType === "favorites"
              ? this.props.store.playlistName
                ? this.props.store.playlistName
                : "My favorites"
              : "Filter By"}
          </h3>

          <span
            className={`mobile-actions-favorites icon dots ${
              this.props.store.searchType === "favorites" ? " active" : ""
            } ${this.props.store.mobileActive ? "mobile-hide" : ""}`}
            onMouseDown={this.props.store.toggleFavoritesMenu}
          />
          {favoritesMenuMobile}
        </div>

        <div
          className={`Search-mobile-interface-selected ${
            this.props.store.searchType === "favorites" ? "mobile-faves" : ""
          }`}
        >
          <div className={"Search-mobile-interface-results"}>
            {this.props.store.searchType === "favorites"
              ? this.props.store.visibleMusic.length
              : this.props.store.musicCount}{" "}
            tracks
          </div>
          <div
            className={`active-tags ${(this.props.store.showingAllSelectedMobile
              ? "active"
              : "") ||
              (this.props.store.searchType === "favorites"
                ? "mobile-hide"
                : "")}`}
          >
            {activeTags}
            <span
              className={"plus-open"}
              onClick={this.props.store.toggleShowAllSelectedMobile}
            >
              {spanVerbiage}
            </span>
            <span
              className={"clear-all"}
              onClick={this.props.store.resetKeywords}
            >
              clear all
            </span>
          </div>
        </div>

        <div
          className={`mobile-playlists ${
            this.props.store.showingPlaylistListing ? "active" : ""
          }`}
        >
          <h3>my playlists</h3>
          <p>{`${existingSearches.length} playlists`}</p>
          <ul>{mobilePlaylists}</ul>
        </div>

        <div className={"Search-mobile-interface-tabs"}>
          <h3>Filter By</h3>
          <ul className={"Search-mobile-interface-tab-nav"}>
            <li
              className={`${
                this.props.store.showingKeywordsOnMobile ? "active" : ""
              }`}
              onClick={this.props.store.showKeywordsMobile}
            >
              keyword
            </li>
            <li
              className={`${
                this.props.store.showingGenresOnMobile ? "active" : ""
              }`}
              onClick={this.props.store.showGenresMobile}
            >
              genre
            </li>
          </ul>

          <div
            className={`Search-mobile-interface-tab ${
              this.props.store.showingKeywordsOnMobile ? "active" : ""
            }`}
          >
            <ul>{keywordElements}</ul>
          </div>
          <div
            className={`Search-mobile-interface-tab ${
              this.props.store.showingGenresOnMobile ? "active" : ""
            }`}
          >
            <ul>{genreElements}</ul>
          </div>
        </div>
        <button
          type={"button"}
          onClick={e => {
            if (this.props.store.mobileReset) {
              e.preventDefault();
              return;
            }
            this.props.store.toggleMobileMenu();
            window.scrollTo({
              top: 0
            });
          }}
        >
          {this.props.store.musicCount == null
            ? "loading..."
            : filterButtonText}
        </button>
      </div>
    );

    if (this.props.store.showingVideoSync && !this.props.store.isCompactLayout) {
      videoSyncTable = (
        <div className={"Search-video-sync-holder"}>
          <div className={"Search-video-sync-screen"}>
            <VideoSync store={this.props.store.videoSyncState} />
          </div>
        </div>
      );
    }

    genresLeftGutter = (
      <div className={`genres-trigger-wrap`}>
        <div
          className={`Search-genres-title Search-grid-container-row-gutter-left ${
            this.props.store.showingGenresOnMobile === true ? "" : "toggled"
          }`}
          // onMouseDown={(e: MouseEvent) => {
          //   this.props.store.toggleGenresMobile();
          // }}
        >
          <Trans>genres</Trans>
        </div>
        <div className={`right`}>
          <div
            className={`Search-genres-title Subgenre-modal-trigger ${
              this.props.store.showingEditGenresOnMobile === true &&
              this.props.store.hasActiveGenres === true
                ? ""
                : "hide"
            }`}
            // onMouseDown={(e: MouseEvent) => {
            //   this.props.store.toggleGenresMobile();
            // }}
          >
            <span>edit genres</span>
          </div>
        </div>
      </div>
    );
    tagsLeftGutter = (
      <div
        className={`Search-tags-title ${
          this.props.store.showingKeywordsOnMobile === true ? "" : "toggled"
        }`}
      >
        <Trans>keywords</Trans>
      </div>
    );
    // Show the search
    genreColumns = this.props.store.genres.map((tags) => {
      let subgenres = [];
      const subgenreWrapper = [];

      tags.map((tag) => {
        if (tag.children != null) {
          subgenres = [];
          for (let j = 0; j < tag.children.length; j += 1) {
            /** @type {Tag} */
            const subGenre = tag.children[j];
            subgenres.push(
              <div
                className={`sub ${
                  this.props.store.isTagActive(subGenre.id) ? "active" : ""
                }`}
                key={`subGenre_${subGenre.id}`}
                onClick={() => this.props.store.toggleTagActive(subGenre.id)}
              >
                <Trans>{subGenre.tag_name.toLowerCase()}</Trans>
              </div>
            );
          }

          subgenreWrapper[tag.id] = (
            <div className={`subgenre-wrapper`} key={tag.tag_name}>
              {subgenres}
            </div>
          );
        }
      });

      const genres = tags.map(tag => {
        const returnWrapper = [];
        const returnInner = (
          <div
            className={`Search-genre${
              this.props.store.isTagActive(tag.id) ? " Search-genre-active" : ""
            }`}
            onClick={() => this.props.store.toggleTagActive(tag.id)}
            title={"tip: add keywords selected genres to refine search"}
          >
            <Trans>{tag.tag_name.toLowerCase()}</Trans>
          </div>
        );

        if (tag.children != null) {
          returnWrapper.push(
            <div
              className={`wrap-around has-children ${
                this.props.store.hoveredGenre == tag.id
                  ? " children-active"
                  : ""
              }`}
              onMouseEnter={e => {
                this.props.store.toggleGenreHover(tag.id);
                e.stopPropagation();
              }}
              onMouseLeave={e => {
                this.props.store.setHoveredGenre(null);
                e.stopPropagation();
              }}
              key={tag.id}
            >
              {returnInner}
              {subgenreWrapper[tag.id]}
            </div>
          );
        } else {
          returnWrapper.push(
            <div className={`wrap-around`} key={tag.id}>
              {returnInner}
            </div>
          );
        }
        return returnWrapper;
      });
      return (
        <div className={"Search-genres-column"} key={tags[0].id}>
          {genres}
        </div>
      );
    });
    const tagList = this.props.store.tags.map((tag) => (
      <td
        className={`Search-tags-cell${
          this.props.store.isTagActive(tag.id) ? " Search-tags-cell-active" : ""
        }`}
        onClick={() => this.props.store.toggleTagActive(tag.id)}
        key={`${tag.id}`}
        title={"select multiple keywords or genres to focus search"}
      >
        <Trans>{tag.tag_name.toLowerCase()}</Trans>
      </td>
    ));
    const totalTags = this.props.store.tags.length;
    const tagListLinks = this.props.store.tags.map((tag, index) => {
      const isActive = this.props.store.isTagActive(tag.id);
      const progress = totalTags > 1 ? index / (totalTags - 1) : 0;
      const gray = Math.round(185 + (242 - 185) * progress);
      const gradientStyle = isActive ? {} : { backgroundColor: `rgb(${gray}, ${gray}, ${gray})` };
      return (
        <a
          className={`Search-tags-cell${isActive ? " Search-tags-cell-active" : ""}`}
          onClick={() => this.props.store.toggleTagActive(tag.id)}
          key={`${tag.id}`}
          title={"select multiple keywords or genres to focus search"}
          style={gradientStyle}
        >
          <Trans>{tag.tag_name.toLowerCase()}</Trans>
        </a>
      );
    });
    const NUM_ROWS = 7;
    const NUM_COLS = 7;
    tagRows = [];
    for (let row = 0; row < NUM_ROWS; row += 1) {
      tagRows.push(
        <tr key={`${row}`}>
          {tagList.slice(row * NUM_COLS, (row + 1) * NUM_COLS)}
        </tr>
      );
    }

    const withSubGenres = this.props.store.activeTagsWithSubgenres;
    if (withSubGenres.length > 0) {
      const rows = [];

      for (let i = 0; i < withSubGenres.length; i += 1) {
        /** @type {Tag} */
        const withSubGenre = withSubGenres[i];
        rows.push(
          <div className={"Search-subgenre-header"} key={withSubGenre.id}>
            <Trans>{withSubGenre.tag_name.toLowerCase()}</Trans>
          </div>
        );
        rows.push(
          <div
            className={"Search-subgenre-header-subtitle"}
            key={`sub-genres-title-${withSubGenre.id}`}
          >
            <Trans>sub genres</Trans>
          </div>
        );

        const childRows = [];
        if (withSubGenre.children != null) {
          for (let j = 0; j < withSubGenre.children.length; j += 1) {
            /** @type {Tag} */ const subGenre = withSubGenre.children[j];
            childRows.push(
              <div
                className={`Search-genre Search-sub-genre${
                  this.props.store.isTagActive(subGenre.id)
                    ? " Search-genre-active"
                    : ""
                }`}
                onClick={(e) => {
                  this.props.store.toggleTagActive(subGenre.id);
                  this.props.store.toggleSubgenre(e, subGenre.id);
                }}
                key={subGenre.id}
              >
                <Trans>{subGenre.tag_name.toLowerCase()}</Trans>
              </div>
            );
          }
        }

        rows.push(
          <div className={`Subgenre-wrap`} key={`subgenre-wrap-${i}`}>
            {childRows}
          </div>
        );
      }

      if (
        this.props.store.isMobileWindow === false &&
        this.props.store.activeTags.length
      ) {
        tagsLeftPadding = <div className={"Search-subgenre-padding"} />;
        tagsLeftGutter = (
          <div className={`Search-subgenre-wrapper`}>
            <div className={"Search-subgenre-holder"}>{rows}</div>
            <div
              className={`Search-tags-title with-subgenres ${
                this.props.store.showingKeywordsOnMobile === true
                  ? ""
                  : "toggled"
              }`}
            >
              <Trans>keywords</Trans>
            </div>
          </div>
        );
      }
    }

    const keywordsTip = null;

    tagsTable = (
      <div className={"Search-tags-table-container"}>
        {keywordsTip}
        <div
          className={`Search-tags-wrapper mobile-hide ${
            this.props.store.showingKeywordsOnMobile === true
              ? ""
              : "mobile-hide"
          }`}
        >
          {tagListLinks}
        </div>
      </div>
    );

    const itemSizes = this.props.store.visibleMusic.map(m => m.getHeight());
    const itemIds = this.props.store.visibleMusic.map(m => m.music.id);

    let musicBody;
    if (this.props.store.showingInstructions()) {
      musicBody = (
        <div className={"Search-initial-instructions"}>
          <Trans>sync catalogue for creative licensing<br/>
          search music by text, link and keyword</Trans>
        </div>
      );
    } else {
      const musicListHeight = this.props.store.musicListHeight;
      const musicListWidth = this.props.store.musicListWidth;
      const loadingMusic = this.props.store.loadingMusic;

      musicBody = (
        <React.Fragment>
          <div className={"Music Search-music-header"}>
            <div className={"Music-inner-container"}>
              <div className={"Music-name-0"}>
                {this.props.store.musicCount != null && (
                  <Trans>search total</Trans>
                )}
                {this.props.store.musicCount == null
                  ? "loading..."
                  : ` ${
                      this.props.store.searchType === "favorites"
                        ? this.props.store.visibleMusic.length
                        : this.props.store.musicCount
                    }`}
              </div>
              <div className={"Music-duration"}>
                <div className={"Search-order-by-container"}>
                  <div
                    className={"Search-length-header"}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.props.store.toggleSortDirection();
                    }}
                  >
                    <div className={"Search-length-text"}>
                      <Trans>length</Trans>
                    </div>
                    <div className={"Search-length-buttons"}>
                      <div
                        className={`Search-length-buttons-upper-triangle${
                          this.props.store.sortDirection === "asc"
                            ? " Search-length-buttons-upper-triangle-active"
                            : ""
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          this.props.store.setSortDirection("asc");
                        }}
                      />
                      <div
                        className={`Search-length-buttons-lower-triangle${
                          this.props.store.sortDirection === "desc"
                            ? " Search-length-buttons-lower-triangle-active"
                            : ""
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          this.props.store.setSortDirection("desc");
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={"Music-rest"} />
              <div className={"Playlist-name-container"}>
                {this.props.store.playlistName}
              </div>
              <div className={"Search-audio-container"}>
                <div
                  className={"Search-audio-track"}
                  onMouseDown={dragPercentage(
                    this.props.store.setMusicVolume,
                    "Search-audio-track"
                  )}
                >
                  <div
                    className={"Search-audio-track-left"}
                    style={{
                      width: `${this.props.store.musicVolume * 100}%`
                    }}
                  />
                  <div
                    className={"Search-audio-track-dot"}
                    style={{
                      left: `${this.props.store.musicVolume * 100}%`
                    }}
                  />
                </div>
                <div
                  className={"Search-audio-icon"}
                  dangerouslySetInnerHTML={{ __html: soundIconSvg }}
                />
              </div>
            </div>
          </div>

          <div
            className={"Search-music-rows-container"}
            ref={r => this.props.store.setMusicContainerDiv(r)}
          >
            <MusicList
              store={this.props.store}
              musicLength={this.props.store.visibleMusic.length}
              musicListScrollTop={this.props.store.musicListScrollTop}
              musicListHeight={musicListHeight}
              musicListWidth={musicListWidth}
              itemSizes={itemSizes}
              itemIds={itemIds}
              loadingMusic={loadingMusic}
            />
            {/*
            <div className={'Search-music-rows'}
                 ref={(r) => this.props.store.setMusicDiv(r)}
                 style={{height: this.props.store.musicListHeight+'px'}}>
              {musicListEntries}
            </div>
            */}
          </div>
        </React.Fragment>
      );
    }

    let albumArt = null;
    /** @type {string | null} */
    const albumArtUrl = MusicTrackState.getActiveAlbumArt();
    if (albumArtUrl != null) {
      albumArt = (
        <img
          src={albumArtUrl}
          onClick={() => {
            /** @type {MusicTrackState | null} */
            const activeTrack =
              MusicTrackState.globallyActiveTrack;
            if (activeTrack != null) {
              this.props.store.setCategory(activeTrack.music.category);
            }
          }}
        />
      );
    }

    const buttons = [];

    let videoSyncTip = null;
    if (this.props.store.showingSyncButtonTip) {
      videoSyncTip = (
        <div className={"Music-play-tip Sync-button-tip"}>
          use video sync to hear our music synced to your video
        </div>
      );
    }

    if (this.props.store.selectedTrack != null) {
      let exportText = this.props.store.videoSyncState.exportText;
      const exportProgress = this.props.store.videoSyncState.exportProgress;
      if (exportProgress > 0) {
        exportText += ` ${exportProgress}%`;
      }

      if (
        this.props.store.selectedTrack.audioArrayBuffer != null &&
        this.props.store.videoSyncState.url
      ) {
        let exportingClass = "Search-button Grey Downloadmix-button";
        if (this.props.store.videoSyncState.exporting) {
          exportingClass = "Search-button Grey Downloadmix-button disabled";
        }
        buttons.push(
          <div
            className={exportingClass}
            onClick={this.props.store.videoSyncState.downloadMixedVideo.bind(
              this
            )}
            key={"36.5"}
          >
            {exportText}
          </div>
        );
      }
    }

    // if (this.props.store.searchType != "favorites") {
    if (this.props.store.hasAnyLikes) {
      buttons.push(
        <div
          className={"Search-button Search-button-primary"}
          onClick={this.props.store.viewAllLikes}
          key={"1.55"}
        >
          <Trans>view favorites</Trans>
        </div>
      );
    }
    let downloadBtnClasses = "Search-button";
      if (
        (this.props.store.hasAnyLikes && this.props.store.hasOriginalLikes) ||
        this.props.store.showDownloadSearch() || 
        this.props.store.playlistName != null
      ) {
        downloadBtnClasses = "Search-button Search-button-primary";

        buttons.push(
          <div
            className={downloadBtnClasses}
            onClick={this.props.store.downloadAll}
            key={"2.7"}
          >
            {this.props.store.showDownloadSearch() ? (
              "download search"
            ) : (
              <Trans>download all likes</Trans>
            )}
          </div>
        );
      }
    // }

    if (
      this.props.store.isAdmin &&
      this.props.store.searchType === "favorites" &&
      this.props.store.playlistName
    ) {
      buttons.push(
        <label
          htmlFor='fileUploadInput'
          className={"Search-button"}
          onClick={event => {
            event.target.value = null;
          }}
          key={"334"}
        >
          {this.props.store.videoSyncState.videoUploadText}
          <input
            id="fileUploadInput"
            className={"fileUploadInput"}
            type={"file"}
            accept="video/*"
            onChange={event => {
              this.props.store.videoSyncState.onFileChange(event);
            }}
            onClick={event => {
              event.target.value = null;
            }}
            key={"333"}
          />
        </label>
      );
      buttons.push(
        <div
          className={"Search-button"}
          onClick={this.props.store.videoSyncState.showVideoList}
          key={"95"}
        >
          Load Uploaded Video
        </div>
      );
      buttons.push(
        <div
          className={"Search-button"}
          onClick={this.props.store.saveVideoDataToPlaylist}
          key={"96"}
        >
          save data
        </div>
      );
      if (this.props.store.hasAnyLikes) {
        buttons.push(
          <div
            className={"Search-button"}
            onClick={this.props.store.clearAllLikes}
            key={"4"}
          >
            <Trans>clear all likes</Trans>
          </div>
        );
      }
    } else if (
      this.props.store.searchType === "search" ||
      this.props.store.searchType === "keyword_search" ||
      this.props.store.searchType === "single_keyword_search" ||
      this.props.store.searchType === "category" ||
      this.props.store.searchType === "similar" ||
      this.props.store.searchType === "ai_search"
    ) {
      if (this.props.store.hasAnyLikes) {
        buttons.push(
          <div
            className={"Search-button"}
            onClick={this.props.store.clearAllLikes}
            key={"4"}
          >
            <Trans>clear all likes</Trans>
          </div>
        );
      }
      if (this.props.store.hasAnyTags()) {
        buttons.push(
          <div
            className={"Search-button"}
            onClick={this.props.store.resetKeywords}
            key={"3"}
          >
            <Trans>reset keywords</Trans>
          </div>
        );
      }
    } else if (this.props.store.searchType === "favorites") {
      if (this.props.store.hasAnyLikes && !this.props.store.hasOriginalLikes) {
        buttons.push(
          <div
            className={"Search-button"}
            onClick={this.props.store.clearAllLikes}
            key={"4"}
          >
            <Trans>clear all likes</Trans>
          </div>
        );
      } else {
        if (this.props.store.hasAnyLikes) {
          buttons.push(
            <div
              className={"Search-button"}
              onClick={this.props.store.clearAllLikes}
              key={"4"}
            >
              <Trans>clear all likes</Trans>
            </div>
          );
        }
      }

      if (this.props.store.hasAnyLikes && this.props.store.hasOriginalLikes) {
        buttons.push(
          <div
            className={"Search-button Grey"}
            onClick={this.props.store.saveSearch}
            key={"2.5"}
          >
            save playlist
          </div>
        );
      }
    }

    if (this.props.store.playlistName) {
      buttons.push(
        <div
          className={"Search-button Grey"}
          onClick={this.props.store.getShortLink}
          key={"1.445"}
        >
          <Trans>get playlist link</Trans>
        </div>
      );
    }
    
    if (this.props.store.hasAnyPlayed) {
      buttons.push(
        <div
          className={"Search-button"}
          onClick={this.props.store.clearAllPlayed}
          key={"5"}
        >
          <Trans>clear all waveforms</Trans>
        </div>
      );
    }
    
    if (this.props.store.isAdmin && this.props.store.music.length > 0) {
      buttons.push(
        <div
          className={"Search-button"}
          onClick={this.props.store.markAllPlayed}
          key={"5.564"}
        >
          show all waveforms
        </div>
      );
    }

    if (
      this.props.store.hasAnyLikes ||
      this.props.store.hasAnyTags() ||
      this.props.store.hasAnyPlayed ||
      this.props.store.videoSyncState.url != null
    ) {
      buttons.push(
        <div
          className={"Search-button"}
          onClick={this.props.store.resetSearch}
          key={"5.5644"}
        >
          <Trans>reset search</Trans>
        </div>
      );
    }

    if (this.props.store.showingVideoSync) {
      buttons.push(
        <div
          className={"Search-button"}
          onClick={this.props.store.hideVideoSync}
          key={"5.565"}
        >
          remove video sync
        </div>
      );
    } else {
      buttons.push(
        <div
          className={"Search-button"}
          onClick={this.props.store.showVideoSync}
          key={"5.565"}
        >
          show video sync
        </div>
      );
    }

    return (
      // Add your component markup and other subcomponent references here.
      <div
        className={`Search${this.props.blur ? " Search-blur" : ""}`}
        ref={r => this.props.store.setRootDiv(r)}
      >
        {mobileSearch}
        <div className={"Search-genres-bar-wrap"}>
          <div className={"Search-genres-bar Search-grid-container-row"}>
            <div className="Search-ai-search-wrapper">
              <svg className="Search-ai-search-trigger-star" xmlns="http://www.w3.org/2000/svg" viewBox="574 259 290 290" width="16" height="16">
                <defs><clipPath id="aistar"><path d="M 719.4375 259.6875 L 731.769531 354.578125 L 786.988281 276.308594 L 754.285156 366.375 L 839.527344 322.414062 L 768.757812 386.746094 L 864.1875 387.816406 L 771.441406 411.941406 L 855.609375 456.976562 L 762.863281 435.53125 L 815.9375 514.875 L 744.097656 452.152344 L 754.285156 547.042969 L 719.4375 458.046875 L 684.589844 547.042969 L 694.777344 452.152344 L 622.9375 514.875 L 676.011719 435.53125 L 583.265625 456.976562 L 667.433594 411.941406 L 574.6875 387.816406 L 670.117188 386.746094 L 599.347656 322.414062 L 684.589844 366.375 L 651.886719 276.308594 L 707.105469 354.578125 Z"/></clipPath></defs>
                <rect x="574" y="259" width="290" height="290" fill="#cfa74d" clipPath="url(#aistar)" />
              </svg>
              <input
                className="Search-ai-search-trigger"
                type="text"
                readOnly
                ref={r => { this._aiSearchTriggerRef = r; }}
                value={this.props.store.aiSearchLoading ? "searching..." : ""}
                placeholder="search by text or paste a music link"
                onClick={() => this.props.store.openAiSearchPanel()}
              />
            </div>
            {genresLeftGutter}
            <div
              className={`Search-genres-grid Search-grid-container-row-main ${
                this.props.store.showingVideoSync ? "" : "wide"
              }`}
            >
              {genreColumns}
            </div>
            <div
              className={
                "Search-genres-right-gutter Search-grid-container-row-gutter-right"
              }
            >
              <div className={"Search-tips"}>
                <Trans>tips</Trans>
                <div className={"Search-tips-popover"}>
                  <hr />
                  <a
                    className={"Search-tips-link"}
                    onClick={this.props.store.showKeywordDefinitions}
                  >
                    <Trans>keyword description</Trans>
                  </a>
                </div>
              </div>

              <div
                className={"language-choice"}
                onClick={() => this.props.store.changeLanguage("en")}
              >
                English
              </div>
              <div
                className={"language-choice"}
                onClick={() => this.props.store.changeLanguage("ja")}
              >
                日本語
              </div>
            </div>
          </div>
        </div>

        <div className={"Search-keywords-bar Search-grid-container-row"}>
          <div
            className={
              "Search-grid-container-row-gutter-left genre-gutter genres-trigger-wrap"
            }
          >
            {tagsLeftGutter}
            <div className={`Search-genres-title Subgenre-modal-trigger`}>
              <span>edit keywords</span>
            </div>
          </div>
          <div className={"Search-grid-container-row-main"}>
            <div className={"Search-grid-container-row-main-inner"}>
              {videoSyncTable}
              {tagsTable}
              <div className={"Search-grid-container-row-gutter-right"}>
                <div className={"Search-button-holder"}>{buttons}</div>
                <div
                  className={`Search-album-art-holder ${
                    this.props.store.isAdmin ? "admin" : ""
                  }`}
                >
                  {albumArt}
                </div>
                <div className={"License-Nudge"}>
                  <div className={"License-Nudge-Inner"}>
                    for information about customizing or licensing our music see
                    <a href={`https://ascheandspencer.com/faqs`}>FAQ</a>
, or
<a href={`mailto:ryan@ascheandspencer.com`}>
                      contact us directly
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {musicBody}
          </div>

          {subgenresMobile}
        </div>
      </div>
    );
  }
}

/**
 * @typedef {Object} SearchProps
 * @property {SearchState} store - The main search state store
 */

@observer
class Search extends Component {
  Capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  componentDidMount() {
    updateCollectionPageSchema(this.props.store);
  }

  componentDidUpdate() {
    updateCollectionPageSchema(this.props.store);
  }

  componentWillUnmount() {
    updateCollectionPageSchema({ searchType: "", playlistName: null });
  }

  render() {
    const { t, i18n } = this.props;
    const contentHeight = this.props.store.getContentHeight();
    let rootDivHeight = 0;
    if (
      this.props.store.rootDiv != null &&
      this.props.store.musicContainerDiv != null
    ) {
      rootDivHeight =
        this.props.store.musicContainerDiv.getBoundingClientRect().top -
        this.props.store.rootDiv.getBoundingClientRect().top;
    }

    let bodyHeight = `${Math.max(contentHeight + rootDivHeight, 410)}px`;
    let bodyOverflow = ``;
    if (this.props.store.isMobileWindow && this.props.store.mobileActive) {
      bodyHeight = `calc(100vh + 100px)`;
      bodyOverflow = `hidden`;
    } else if (this.props.store.isMobileWindow) {
      bodyHeight = `auto`;
    }

    let confirmActionPopup = null;
    let chooseMusicType = null;
    let loginFlowPopup = null;
    let addToPlaylistPopup = null;
    let sharePopup = null;
    let myAccountPopup = null;
    let searchLikeTrackPopup = null;
    let shortLinkPopup = null;
    let tosPopup = null;
    let tipPopup = null;
    let keywordDefinitionsPopup = null;
    let uploadTrackPopup = null;
    let tutorialPopup = null;
    let initialNotePopup = null;
    let videoSyncErrorPopup = null;
    let passwordPopup = null;

    // this.props.store.requirePassword &&
    if (this.props.store.showingPasswordPopup) {
      passwordPopup = (
        <Popup playlistPassword>
          <div className={"Search-initial-note-paragraph"}>
            this playlist requires a password to view
          </div>
          <input
            type={"password"}
            placeholder={`type password here`}
            onChange={e => {
              this.props.store.setPasswordForPlaylist(e.target.value);
            }}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                this.props.store.checkPassword();
              }
            }}
          />
          <div
            className={`Search-initial-note-paragraph ${
              this.props.store.incorrectPassword ? "incorrect" : "hide"
            }`}
          >
            incorrect password
          </div>
          <button
            className={"Search-popup-close-button"}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.checkPassword();
            }}
          >
            submit
          </button>
        </Popup>
      );
    }
    if (this.props.store.showingInitialNote) {
      initialNotePopup = (
        <Popup onClose={this.props.store.closeInitialNote} initialNote>
          <div className={"Search-initial-note-paragraph"}>
            welcome to{" "}
            <span className={"Search-initial-note-paragraph-highlight"}>
              version 4
            </span>{" "}
            of our music search. a year in the making, this complete
            reengineering of our site offers lightning fast search and playback
            speed, improved search algorithm, and a new video sync feature
            allowing you the ability to align all of the music in our collection
            with any video on your computer.
          </div>
          <div className={"Search-initial-note-paragraph"}>
            your input is vital to the progression of our site. if you
            experience any issues with{" "}
            <span className={"Search-initial-note-paragraph-highlight"}>
              version 4
            </span>{" "}
            please contact me directly so that we can fix the problem
            immediately.
          </div>
          <div
            className={
              "Search-initial-note-paragraph Search-initial-note-signature"
            }
          >
            <div>thad spencer</div>
            <div>
              <a href={"mailto:thad@ascheandspencer.com"}>
                thad@ascheandspencer.com
              </a>
            </div>
          </div>
          <button
            className={"Search-popup-close-button"}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              this.props.store.closeInitialNote();
            }}
          >
            <Trans>close</Trans>
          </button>
        </Popup>
      );
    } else if (this.props.store.showingLoginFlow) {
      loginFlowPopup = (
        <Popup onClose={this.props.store.loginFlowState.close} login>
          <LoginFlow store={this.props.store.loginFlowState} />
        </Popup>
      );
    } else if (this.props.store.showingAddToPlaylistPopup) {
      addToPlaylistPopup = (
        <Popup onClose={this.props.store.addToPlaylistState.close} playlist>
          <AddToPlaylist store={this.props.store.addToPlaylistState} />
        </Popup>
      );
    } else if (this.props.store.showingSharePopup) {
      sharePopup = (
        <Popup onClose={this.props.store.toggleSharePopup} share>
          <Share store={this.props.store.shareState} />
        </Popup>
      );
    } else if (this.props.store.showingMyAccount) {
      myAccountPopup = (
        <Popup
          onClose={this.props.store.myAccountState.close}
          login={this.props.store.myAccountState.currentPage === "save"}
          fullHeight={this.props.store.myAccountState.currentPage === "list"}
        >
          <MyAccount store={this.props.store.myAccountState} />
        </Popup>
      );
    } else if (this.props.store.searchingLikeTrack != null) {
      const track = this.props.store.searchingLikeTrack;
      const availableTags = allTags.filter(
        (tag) =>
          track.music != null &&
          track.music[tag.urlsafe_name] != null &&
          track.music[tag.urlsafe_name] > 0
      );

      const tagButtons = availableTags.map(tag => {
        const tagActive = this.props.store.searchLikeTrackTagActive(tag.id);
        return (
          <div
            key={tag.id}
            onClick={() => this.props.store.toggleSearchLikeTrackTag(tag.id)}
            className={`Search-by-track-popup-tag-button${
              tagActive ? " Search-by-track-popup-tag-button-active" : ""
            }`}
          >
            {tag.tag_name}
          </div>
        );
      });

      searchLikeTrackPopup = (
        <Popup
          onClose={() => this.props.store.showRelevantTags(null)}
          searchLikeTag
        >
          <div className={"Search-by-track-popup-name"}>{track.music.name}</div>
          <div className={"Search-by-track-popup-tag-holder"}>{tagButtons}</div>
          <div className={"Search-by-track-popup-instructions"}>
            select relevant tags to find music similar to this track
            <br />
            click search to perform a new search using your selected tags
          </div>
          <div className={"Search-by-track-popup-button-holder"}>
            <div
              className={"Popup-button"}
              onClick={() => this.props.store.showRelevantTags(null)}
            >
              <Trans>cancel</Trans>
            </div>
            <div
              className={"Popup-button"}
              onClick={this.props.store.commitSearchLikeTrack}
            >
              <Trans>search</Trans>
            </div>
          </div>
        </Popup>
      );
    } else if (this.props.store.showingKeywordDefinitions) {
      keywordDefinitionsPopup = (
        <Popup onClose={this.props.store.hideKeywordDefinitions}>
          <div className={"Search-keywords-table-holder"}>
            <table className={"Search-keywords-table"}>
              <tr>
                <td>
                  <Trans>action</Trans>
                </td>
                <td>
                  <Trans>action-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>anthemic</Trans>
                </td>
                <td>
                  <Trans>anthemic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>bold</Trans>
                </td>
                <td>
                  <Trans>bold-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>builds</Trans>
                </td>
                <td>
                  <Trans>builds-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>calm</Trans>
                </td>
                <td>
                  <Trans>calm-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>cinematic</Trans>
                </td>
                <td>
                  <Trans>cinematic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>comedic</Trans>
                </td>
                <td>
                  <Trans>comedic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>confident</Trans>
                </td>
                <td>
                  <Trans>confident-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>dark</Trans>
                </td>
                <td>
                  <Trans>dark-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>delicate</Trans>
                </td>
                <td>
                  <Trans>delicate-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>dreamy</Trans>
                </td>
                <td>
                  <Trans>dreamy-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>driving</Trans>
                </td>
                <td>
                  <Trans>driving-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>elevator</Trans>
                </td>
                <td>
                  <Trans>elevator-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>energetic</Trans>
                </td>
                <td>
                  <Trans>energetic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>epic</Trans>
                </td>
                <td>
                  <Trans>epic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>fashion</Trans>
                </td>
                <td>
                  <Trans>fashion-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>gritty</Trans>
                </td>
                <td>
                  <Trans>gritty-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>heroic</Trans>
                </td>
                <td>
                  <Trans>heroic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>holiday</Trans>
                </td>
                <td>
                  <Trans>holiday-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>hopeful</Trans>
                </td>
                <td>
                  <Trans>hopeful-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>indie</Trans>
                </td>
                <td>
                  <Trans>indie-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>innocent</Trans>
                </td>
                <td>
                  <Trans>innocent-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>inspirational</Trans>
                </td>
                <td>
                  <Trans>inspirational-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>ironic</Trans>
                </td>
                <td>
                  <Trans>ironic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>anthemic</Trans>
                </td>
                <td>
                  <Trans>anthemic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>lighter side</Trans>
                </td>
                <td>
                  <Trans>lighterside-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>lush</Trans>
                </td>
                <td>
                  <Trans>lush-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>magical</Trans>
                </td>
                <td>
                  <Trans>magical-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>minimal</Trans>
                </td>
                <td>
                  <Trans>minimal-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>mischievous</Trans>
                </td>
                <td>
                  <Trans>mischievous-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>mnemonic</Trans>
                </td>
                <td>
                  <Trans>mnemonic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>mysterious</Trans>
                </td>
                <td>
                  <Trans>mysterious-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>organic</Trans>
                </td>
                <td>
                  <Trans>organic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>playful</Trans>
                </td>
                <td>
                  <Trans>playful-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>positive</Trans>
                </td>
                <td>
                  <Trans>positive-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>pulsating</Trans>
                </td>
                <td>
                  <Trans>pulsating-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>quirky</Trans>
                </td>
                <td>
                  <Trans>quirky-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>retro</Trans>
                </td>
                <td>
                  <Trans>retro-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>romantic</Trans>
                </td>
                <td>
                  <Trans>romantic-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>sad</Trans>
                </td>
                <td>
                  <Trans>sad-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>seductive</Trans>
                </td>
                <td>
                  <Trans>seductive-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>sentimental</Trans>
                </td>
                <td>
                  <Trans>sentimental-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>sneaky</Trans>
                </td>
                <td>
                  <Trans>sneaky-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>sophisticated</Trans>
                </td>
                <td>
                  <Trans>sophisticated-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>sports</Trans>
                </td>
                <td>
                  <Trans>sports-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>happy</Trans>
                </td>
                <td>
                  <Trans>happy-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>tension</Trans>
                </td>
                <td>
                  <Trans>tension-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>thoughtful</Trans>
                </td>
                <td>
                  <Trans>thoughtful-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>tough</Trans>
                </td>
                <td>
                  <Trans>tough-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>twang</Trans>
                </td>
                <td>
                  <Trans>twang-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>upbeat</Trans>
                </td>
                <td>
                  <Trans>upbeat-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>vocal</Trans>
                </td>
                <td>
                  <Trans>vocal-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>warm</Trans>
                </td>
                <td>
                  <Trans>warm-description</Trans>
                </td>
              </tr>
              <tr>
                <td>
                  <Trans>whimsical</Trans>
                </td>
                <td>
                  <Trans>whimsical-description</Trans>
                </td>
              </tr>
            </table>
          </div>
          <div className={"Search-by-track-popup-button-holder"}>
            <div
              className={"Popup-button"}
              onClick={this.props.store.hideKeywordDefinitions}
            >
              <Trans>close</Trans>
            </div>
          </div>
        </Popup>
      );
    } else if (this.props.store.showingVideoSyncError) {
      videoSyncErrorPopup = (
        <Popup onClose={this.props.store.hideVideoSyncError} small>
          <div className={"Search-popup-text"}>
            This file format is currently not supported in Video Sync.
            <br />
            For best results, please choose an H.264 MP4 video file.
          </div>
          <div className={"Search-by-track-popup-button-holder"}>
            <div
              className={"Popup-button"}
              onClick={this.props.store.hideVideoSyncError}
            >
              <Trans>close</Trans>
            </div>
          </div>
        </Popup>
      );
    }

    if (this.props.store.confirmActionCallback != null) {
      confirmActionPopup = (
        <Popup onClose={this.props.store.closeClearAllLikesPopup} confirmAction>
          <div className={"Search-popup-text"}>
            {this.props.store.confirmActionMessage}
          </div>
          <div className={"Search-by-track-popup-button-holder"}>
            <div
              className={"Popup-button"}
              onClick={() => {
                this.props.store.confirmActionCallback();
              }}
            >
              <Trans>yes</Trans>
            </div>
            <div
              className={"Popup-button"}
              onClick={() => this.props.store.closeConfirmActionPopup()}
            >
              <Trans>cancel</Trans>
            </div>
          </div>
        </Popup>
      );
    }

    if (this.props.store.tipMessage != null) {
      tipPopup = (
        <Popup onClose={this.props.store.closeTip} small red>
          {this.props.store.tipMessage}
          <div className={"Search-by-track-popup-button-holder"}>
            <div
              className={"Popup-button"}
              onClick={() => this.props.store.closeTip()}
            >
              close
            </div>
          </div>
        </Popup>
      );
    }

    if (this.props.store.musicTypesCallback != null) {
      chooseMusicType = (
        <Popup onClose={this.props.store.closeMusicTypesPopup} small>
          <div className={"Search-popup-text"}>
            Please choose audio file format below
            <br />
            192kbps mp3 or hi-res 48k / 24bit wav file
            <br />
            please note: choosing wav file increases download time
          </div>
          <div className={"Search-by-track-popup-button-holder"}>
            <div
              className={"Popup-button"}
              onClick={() => this.props.store.musicTypesCallback("mp3")}
            >
              mp3
            </div>
            <div
              className={"Popup-button"}
              onClick={() => {
                this.props.store.musicTypesCallback("wav");
              }}
            >
              wav
            </div>
          </div>
        </Popup>
      );
    }

    if (
      this.props.store.shortLink != null &&
      !this.props.store.isMobileWindow
    ) {
      shortLinkPopup = (
        <Popup
          onClose={() => this.props.store.clearShortLink()}
          small
          shortLink
        >
          <div className={"Search-short-link-header"}>
            {`${this.Capitalize(this.props.store.shortLinkType)} Link`}
          </div>
          <input
            readOnly
            className={"Search-short-link-text"}
            type={"text"}
            onFocus={e => {
              e.target.select();
            }}
            ref={(r) => {
              if (r != null) r.focus();
            }}
            defaultValue={this.props.store.shortLink}
          />
        </Popup>
      );
    }

    if (this.props.store.tosCallback != null) {
      tosPopup = (
        <Popup
          onClose={() => this.props.store.rejectTOS()}
          small
          termsOfService
        >
          <div className={"Search-popup-text"}>
            <Trans>agreement</Trans>{" "}
            <a href={"mailto:licensing@ascheandspencer.com"}>
              Asche & Spencer Music
            </a>
            .
          </div>
          <div className={"Search-by-track-popup-button-holder"}>
            <div
              className={"Popup-button"}
              onClick={() => this.props.store.rejectTOS()}
            >
              <Trans>cancel</Trans>
            </div>
            <div
              className={"Popup-button"}
              onClick={() => this.props.store.confirmTOS()}
            >
              <Trans>accept</Trans>
            </div>
          </div>
        </Popup>
      );
    }

    if (this.props.store.showingUpload) {
      uploadTrackPopup = (
        <Popup
          onClose={() => this.props.store.hideUploadWindow()}
          small={false}
          fullHeight={false}
        >
          <UploadTrack store={this.props.store.uploadTrackState} />
        </Popup>
      );
    }

    let adminTrackEditorPopout = null;
    if (this.props.store.showingAdminTrackEditor) {
      adminTrackEditorPopout = (
        <AdminTrackEditor store={this.props.store.adminTrackEditorState} />
      );
    }



    /** @type {boolean} */
    const blur =
      confirmActionPopup != null ||
      chooseMusicType != null ||
      tipPopup != null ||
      loginFlowPopup != null ||
      addToPlaylistPopup != null ||
      sharePopup != null ||
      myAccountPopup != null ||
      searchLikeTrackPopup != null ||
      keywordDefinitionsPopup != null ||
      uploadTrackPopup != null ||
      shortLinkPopup != null ||
      tosPopup != null ||
      initialNotePopup != null ||
      videoSyncErrorPopup != null ||
      passwordPopup != null;

    let autocompleteTray = null;
    let searchBarContents = this.props.store.searchBarContents;

    if (
      this.props.store.searchBarHasFocus &&
      this.props.store.searchBarContents.length > 0
    ) {
      if (this.props.store.autocompleteTracks.length > 0) {
        if (
          this.props.store.autocompleteHighlight >= 0 &&
          this.props.store.autocompleteReplaceContents
        ) {
          searchBarContents = this.props.store.autocompleteTracks[
            this.props.store.autocompleteHighlight
          ];
        }

        const autocompleteEntries = this.props.store.autocompleteTracks.map(
          (track, index) => (
            <div
              className={`Search-site-header-autocomplete-entry${
                index === this.props.store.autocompleteHighlight
                  ? " Search-site-header-autocomplete-entry-highlight"
                  : ""
              }`}
              key={track.toString()}
              onMouseDown={() => {
                this.props.store.setSearchBarContents(track);
                this.props.store.doKeywordSearch(
                  this.props.store.searchBarContents
                );
              }}
              onMouseOver={
                this.props.store.isMobileWindow
                  ? () => false
                  : this.props.store.setAutocompleteHighlight(index)
              }
            >
              {track
                .toString()
                .toLowerCase()
                .substring(0, this.props.store.searchBarContents.length)}
              <b>
                {track
                  .toString()
                  .toLowerCase()
                  .substring(this.props.store.searchBarContents.length)}
              </b>
            </div>
          )
        );
        autocompleteTray = (
          <div className={"Search-site-header-autocomplete-holder"}>
            {autocompleteEntries}
          </div>
        );
      } else if (this.props.store.autocompleteDisabledTrack) {
        autocompleteTray = (
          <div className={"Search-site-header-autocomplete-holder"}>
            <div
              className={
                "Search-site-header-autocomplete-entry Search-site-header-autocomplete-error"
              }
            >
              this track is currently unavailable.
            </div>
          </div>
        );
      } else if (!this.props.store.searchBarLoadingAutocomplete) {
        autocompleteTray = (
          <div className={"Search-site-header-autocomplete-holder"}>
            <div
              className={
                "Search-site-header-autocomplete-entry Search-site-header-autocomplete-error"
              }
            >
              this search bar is for finding asche & spencer tracks by name. try
              searching using one of our track titles like organic 04 instead
            </div>
          </div>
        );
      }
    }

    let loginNav = null;
    if (!this.props.store.isMobileWindow) {
      if (this.props.store.loggedIn) {
        loginNav = (
          <div className={"login-nav"}>
            <a
              className={"account-trigger"}
              title={"My Account"}
              href={""}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                this.props.store.showMyAccount();
              }}
            />
          </div>
        );
      } else {
        loginNav = (
          <div className={"login-nav"}>
            <a
              className={""}
              title={"Log in"}
              href={""}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.props.store.showLoginFlow();
              }}
            >
              log in
            </a>
            <a
              className={""}
              title={"Sign up"}
              href={""}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                this.props.store.showLoginFlow();
                this.props.store.loginFlowState.toCreateAccount();
              }}
            >
              sign up
            </a>
          </div>
        );
      }
    }
    /*
    let carousel = null;
    if (this.props.store.showingInstructions()) {
      carousel = <Carousel />;
    }
     */

    let adminTools = null;
    if (this.props.store.isAdmin) {
      adminTools = (
        <div className={"Search-admin-tools-tray"}>
          <div className={"Search-admin-tools-title"}>Admin Tools</div>
          <div className={"Search-admin-tools-buttons"}>
            <div className={"Search-site-header-container-top-row-links"}>
              <a
                href={""}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.props.store.showUploadWindow();
                }}
              >
                upload
              </a>
              <a
                href={""}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.props.store.viewAllDisabled();
                }}
              >
                all disabled
              </a>
              <a
                href={""}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.props.store.viewUntagged();
                }}
              >
                untagged
              </a>
            </div>
          </div>
        </div>
      );
    }

    return (
      // Add your component markup and other subcomponent references here.
      <div>
        {searchLikeTrackPopup}
        {keywordDefinitionsPopup}
        {loginFlowPopup}
        {myAccountPopup}
        {addToPlaylistPopup}
        {sharePopup}
        {tipPopup}
        {shortLinkPopup}
        {uploadTrackPopup}
        {chooseMusicType}
        {tosPopup}
        {confirmActionPopup}
        {adminTrackEditorPopout}
        {tutorialPopup}
        {initialNotePopup}
        {passwordPopup}
        {videoSyncErrorPopup}
        <div className={`Search-site-header${blur ? " Search-blur" : ""}`}>
          <div className={"Search-site-header-container"}>
            <div className={"Search-site-header-container-top-row"}>
              <div
                className={`Search-site-header-mobile-profile ${
                  this.props.store.mobileProfileMenuOpen ? "active" : ""
                }`}
              >
                <span
                  className={"Search-site-header-trigger"}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.props.store.toggleProfileMenu();
                  }}
                />
                <ul className={"mobile-dropdown"}>
                  <li
                    className={"icon favorites"}
                    onClick={(e) => {
                      this.props.store.toggleProfileMenu();
                      this.props.store.viewAllLikes();
                    }}
                  >
                    My favorites
                  </li>
                  <li
                    className={"icon playlists"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.props.store.toggleProfileMenu();
                      this.props.store.togglePlaylistListing();
                    }}
                  >
                    My playlists
                  </li>
                  <li
                    className={"icon logout"}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.props.store.toggleProfileMenu();
                      this.props.store.myAccountState.logout();
                    }}
                  >
                    Log out
                  </li>
                </ul>
              </div>
              <div
                className={`Search-site-header-container-top-row-logo ${
                  this.props.store.menuOpen ? "mobile-hide" : ""
                }`}
              >
                <a href={"https://ascheandspencer.com/"}>
                  <img
                    className={"Search-site-header-logo"}
                    src={headerLogoPng}
                    alt='Asche & Spencer Logo'
                  />
                </a>
                {loginNav}
              </div>
              {/* <div className={"Search-site-header-container-top-row-spacer"} /> */}
              <div
                className={"menu-wrapper"}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  this.props.store.toggleBurgerMenu();
                }}
              >
                <div
                  className={`hamburger-menu ${
                    this.props.store.menuOpen ? "animate" : ""
                  }`}
                />
              </div>
              <div
                className={`Search-site-header-container-top-row-links ${
                  this.props.store.menuOpen ? "open" : ""
                }`}
              >
                <div className={"nav"}>
                  <a href={"https://ascheandspencer.com/who-we-are"}>
                    <Trans>who we are</Trans>
                  </a>
                  <a href={"https://ascheandspencer.com/#reel"}>
                    <Trans>reel</Trans>
                  </a>
                  <a href={"https://ascheandspencer.com/how-we-work"}>
                    <Trans>how we work</Trans>
                  </a>
                  <a href={"https://ascheandspencer.com/news"}>
                    <Trans>news</Trans>
                  </a>
                  <a href={"https://ascheandspencer.com/contact-us"}>
                    <Trans>contact</Trans>
                  </a>
                  {/* <a>
                    <Trans>music collection</Trans>
                  </a> */}
                  <a href={"https://www.ascheandspencer.com/playlists"}>
                    curated playlists
                  </a>
                  {this.props.store.loggedIn ? (
                    <a
                      href={""}
                      className="my-playlists"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        this.props.store.showMyAccount();
                      }}
                    >
                      my playlists
                    </a>
                  ) : (
                    ""
                  )}
                </div>

                <div className={"Search-site-header-container-bottom-row"}>
                  <input
                    className={"Search-site-header-search-bar"}
                    type={"text"}
                    placeholder={t("track title")}
                    value={searchBarContents}
                    autoComplete={"off"}
                    autoCorrect={"off"}
                    autoCapitalize={"off"}
                    spellCheck='false'
                    onChange={e => {
                      this.props.store.setSearchBarContents(e.target.value);
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        this.props.store.doKeywordSearch(searchBarContents);
                      }
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        this.props.store.moveAutocompleteHighlightDown();
                      }
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        this.props.store.moveAutocompleteHighlightUp();
                      }
                      e.stopPropagation();
                    }}
                    onFocus={() => this.props.store.setSearchBarHasFocus(true)}
                    onBlur={() => this.props.store.setSearchBarHasFocus(false)}
                    ref={r => {
                      this.props.store.searchBarRef = r;
                    }}
                  />
                  {autocompleteTray}
                </div>
              </div>
            </div>
            {adminTools}
          </div>
        </div>

        {/* AI search panel + backdrop (rendered outside genres bar stacking context) */}
        {this.props.store.aiSearchPanelOpen && (
          <>
            <div className="Search-ai-search-backdrop" onClick={() => this.props.store.closeAiSearchPanel()} />
            <div
              className="Search-ai-search-panel"
              ref={r => { this.props.store.aiSearchPanelRef = r; }}
              style={(() => {
                const triggerRect = this._aiSearchTriggerRef?.getBoundingClientRect();
                if (!triggerRect) return {};
                return { top: triggerRect.bottom + 8 };
              })()}
            >
              <div className="Search-ai-search-panel-input-row">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#999" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  className="Search-ai-search-panel-input"
                  type="text"
                  placeholder="search by text or paste a music link"
                  value={this.props.store.aiSearchQuery}
                  autoFocus
                  onChange={(e) => { this.props.store.aiSearchQuery = e.target.value; }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") {
                      e.preventDefault();
                      this.props.store.doAiSearch(this.props.store.aiSearchQuery);
                    }
                    if (e.key === "Escape") {
                      this.props.store.closeAiSearchPanel();
                    }
                  }}
                />
                <label className="Search-ai-search-panel-vocals">
                  <span>ignore vocals</span>
                  <input
                    type="checkbox"
                    checked={this.props.store.aiSearchIgnoreVocals}
                    onChange={(e) => { this.props.store.aiSearchIgnoreVocals = e.target.checked; }}
                  />
                </label>
              </div>

              <div className="Search-ai-search-panel-prompts">
                <div className="Search-ai-search-panel-prompt">
                  <div className="Search-ai-search-panel-prompt-title">write a music prompt</div>
                  <div className="Search-ai-search-panel-prompt-example">"stylish orchestral music with beats"</div>
                </div>
                <div className="Search-ai-search-panel-prompt">
                  <div className="Search-ai-search-panel-prompt-title">use a reference track</div>
                  <div className="Search-ai-search-panel-prompt-example">link from youtube, spotify, tiktok or apple</div>
                </div>
                <div className="Search-ai-search-panel-prompt">
                  <div className="Search-ai-search-panel-prompt-title">describe a scene</div>
                  <div className="Search-ai-search-panel-prompt-example">"a fast-paced car chase in a heist movie"</div>
                </div>
                <div
                  className="Search-ai-search-panel-search-btn"
                  onClick={() => this.props.store.doAiSearch(this.props.store.aiSearchQuery)}
                >
                  search
                </div>
              </div>

              {this.props.store.loggedIn && this.props.store.aiSearchRecentSearches.length > 0 && (
                <div className="Search-ai-search-panel-recents">
                  <div className="Search-ai-search-panel-recents-title">recent searches</div>
                  {this.props.store.aiSearchRecentSearches.map((search, i) => (
                    <div
                      key={i}
                      className="Search-ai-search-panel-recent-item"
                    >
                      <span className="Search-ai-search-panel-recent-icon">&ldquo;&rdquo;</span>
                      <span
                        className="Search-ai-search-panel-recent-text"
                        onClick={() => this.props.store.doAiSearch(search.text)}
                      >
                        {search.text}
                      </span>
                      <button
                        className="Search-ai-search-panel-recent-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          this.props.store.aiSearchQuery = search.text;
                        }}
                        title="Edit this search"
                      >
                        ✎
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* <div className={`VideoSync-preload`}>
          <video playsInline autoPlay muted loop>
            <source
              src='https://search.ascheandspencer.com/32372512-hd.mp4'
              type='video/mp4'
            />
          </video>
        </div> */}

        <div
          className={"Search-root-container"}
          ref={r => this.props.store.setRootContainer(r)}
          style={{
            height: bodyHeight,
            overflowY: bodyOverflow
          }}
        >
          <SearchInternals store={this.props.store} blur={blur} />
        </div>
      </div>
    );
  }
}

export default withTranslation()(Search);
